// Receipt Review modal (slice 4f). Opens from a receipt row's
// "Review" button on the Inventory → Receipts tab. Responsibilities:
//   1. On open, trigger an OCR extract (cache-first). The trigger is
//      automatic so the modal renders the extracted header + line items
//      in a single user action -- no separate "Extract" click after
//      opening. "Re-extract" button bypasses cache.
//   2. Show extracted header (vendor / date / total / shipping / tax)
//      for reference. Header overwrite-to-receipt is intentionally
//      OUT of scope for this slice -- the operator can update the
//      receipt manually via the existing upload-form path. Mixing both
//      bidirectional edits in one slice was the bigger risk-to-value
//      ratio tradeoff.
//   3. Render each extracted line as an ExtractedLineRow with the
//      server-side material suggestion pre-applied. Per-row Post
//      writes a cost entry with receipt_id set.
//   4. Show "already-posted" entries for this receipt (one quick fetch
//      of the changelog with receipt_id filter) so operators don't
//      double-post on a re-open.
//
// Materials catalog is loaded via the parent's `useManufacturingMaterials`
// and passed in -- avoids a second fetch when the Receipts tab is
// already mounted.
import { useEffect, useMemo, useRef, useState } from "react";
import { mfgModalShellClass, mfgXsSecondaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  dismissReceiptUnposted,
  extractManufacturingReceipt,
  listManufacturingInventoryChangelog,
  listManufacturingVendorSuggestions,
  postMaterialCostEntry,
  updateManufacturingReceiptHeader,
  type MfgMaterial,
} from "@/lib/manufacturingClient";
import type {
  MfgInventoryChangelogRow,
  MfgInventoryReceipt,
  MfgReceiptExtractedShape,
  MfgReceiptLineMatchSuggestion,
} from "@/lib/manufacturing/types";
import { convertOcrReceiptQtyToCanonical } from "@/lib/manufacturing/receiveQtyConversion";
import {
  draftFromExtractedLine,
  type ExtractedLineDraft,
} from "./ManufacturingReceiptExtractedLineRow";
import { ManufacturingReceiptExtractionBody } from "./ManufacturingReceiptExtractionBody";
import { ManufacturingReceiptHeaderEditors } from "./ManufacturingReceiptHeaderEditors";
import { InlineHelp } from "@/components/common/InlineHelp";

type Props = {
  receipt: MfgInventoryReceipt;
  materials: MfgMaterial[];
  onClose: () => void;
  /** Called whenever a post or re-extract changes server state, so the
   *  parent can refresh the receipts list (extracted_at chip etc.). */
  onReceiptMutated?: () => void;
  /** Called when the operator creates a new material inline from a
   *  line row; lets the parent refresh its materials catalog so the
   *  new material is visible to future receive lines / receipts. */
  onMaterialCreated?: (material: MfgMaterial) => void;
};

export function ManufacturingReceiptExtractionModal({
  receipt,
  materials,
  onClose,
  onReceiptMutated,
  onMaterialCreated,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [reextracting, setReextracting] = useState(false);
  const [extracted, setExtracted] = useState<MfgReceiptExtractedShape | null>(null);
  const [suggestions, setSuggestions] = useState<Array<MfgReceiptLineMatchSuggestion | null>>([]);
  const [drafts, setDrafts] = useState<ExtractedLineDraft[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [alreadyPosted, setAlreadyPosted] = useState<MfgInventoryChangelogRow[]>([]);
  const [workingIdx, setWorkingIdx] = useState<number | null>(null);
  const [viewUrl, setViewUrl] = useState<string | null>(receipt.view_url ?? receipt.public_url ?? null);
  const [vendorDraft, setVendorDraft] = useState<string>(receipt.vendor_name ?? "");
  const [vendorSavedAt, setVendorSavedAt] = useState<string | null>(null);
  const [vendorSuggestions, setVendorSuggestions] = useState<string[]>([]);
  const [shippingDraft, setShippingDraft] = useState<string>(
    String(receipt.shipping_cost ?? 0)
  );
  const [taxDraft, setTaxDraft] = useState<string>(String(receipt.tax_amount ?? 0));
  const [amountsSavedAt, setAmountsSavedAt] = useState<string | null>(null);
  // Cohort-local catalog -- starts from the parent's `materials` and
  // is augmented with any materials the operator creates inline. We
  // also propagate created materials up via onMaterialCreated so the
  // parent's catalog refreshes for the next session.
  const [localMaterials, setLocalMaterials] = useState<MfgMaterial[]>(materials);
  const lastReceiptId = useRef<string>("");

  useEffect(() => {
    setLocalMaterials(materials);
  }, [materials]);

  useEffect(() => {
    setVendorDraft(receipt.vendor_name ?? "");
    setShippingDraft(String(receipt.shipping_cost ?? 0));
    setTaxDraft(String(receipt.tax_amount ?? 0));
  }, [receipt.id, receipt.vendor_name, receipt.shipping_cost, receipt.tax_amount]);

  useEffect(() => {
    if (lastReceiptId.current === receipt.id) return;
    lastReceiptId.current = receipt.id;
    void runExtract({ force: false });
    void loadPosted();
    void loadVendorSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipt.id]);

  async function loadVendorSuggestions() {
    const r = await listManufacturingVendorSuggestions();
    if (r.ok) setVendorSuggestions(r.data.vendors.map((v) => v.name));
  }

  async function commitVendor(next: string) {
    const trimmed = next.trim();
    const original = (receipt.vendor_name ?? "").trim();
    if (trimmed === original) return;
    const r = await updateManufacturingReceiptHeader(receipt.id, { vendor_name: trimmed });
    if (!r.ok) {
      setError(`Could not save vendor: ${r.error}`);
      return;
    }
    setVendorDraft(r.data.receipt.vendor_name);
    setVendorSavedAt(new Date().toISOString());
    onReceiptMutated?.();
    void loadVendorSuggestions();
  }

  async function commitAmount(field: "shipping_cost" | "tax_amount", raw: string) {
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setError(`Could not save ${field}: must be zero or positive.`);
      return;
    }
    const original =
      field === "shipping_cost" ? receipt.shipping_cost ?? 0 : receipt.tax_amount ?? 0;
    if (Math.abs(parsed - original) < 0.005) return;
    const r = await updateManufacturingReceiptHeader(receipt.id, { [field]: parsed });
    if (!r.ok) {
      setError(`Could not save ${field}: ${r.error}`);
      return;
    }
    if (field === "shipping_cost") setShippingDraft(String(r.data.receipt.shipping_cost));
    else setTaxDraft(String(r.data.receipt.tax_amount));
    setAmountsSavedAt(new Date().toISOString());
    onReceiptMutated?.();
  }

  function handleMaterialCreated(m: MfgMaterial) {
    setLocalMaterials((prev) => (prev.some((x) => x.id === m.id) ? prev : [m, ...prev]));
    onMaterialCreated?.(m);
  }

  async function loadPosted() {
    const r = await listManufacturingInventoryChangelog({
      receipt_id: receipt.id,
      limit: 50,
    });
    if (r.ok) setAlreadyPosted(r.data.entries);
  }

  async function runExtract(opts: { force: boolean }) {
    if (opts.force) setReextracting(true);
    else setLoading(true);
    setError(null);
    const r = await extractManufacturingReceipt(receipt.id, { force: opts.force });
    if (opts.force) setReextracting(false);
    else setLoading(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setExtracted(r.data.extracted);
    setSuggestions(r.data.suggestions);
    setDrafts(
      r.data.extracted.line_items.map((line, i) =>
        draftFromExtractedLine(line, r.data.suggestions[i] ?? null)
      )
    );
    if (r.data.view_url) setViewUrl(r.data.view_url);
    // Slice 4h: autofill receipt-row shipping / tax when the upload
    // form left them at 0 AND OCR pulled non-zero values. Saves the
    // operator a manual edit on the common path (Home Depot OCR with
    // empty shipping at upload time). We never overwrite a non-zero
    // saved value -- if the operator typed something at upload, that
    // wins (they presumably had ground truth from the paper receipt).
    void autofillAmountsFromExtracted(r.data.extracted);
    onReceiptMutated?.();
  }

  async function autofillAmountsFromExtracted(ex: MfgReceiptExtractedShape) {
    const patch: { shipping_cost?: number; tax_amount?: number } = {};
    if (!receipt.shipping_cost && ex.shipping > 0) patch.shipping_cost = ex.shipping;
    if (!receipt.tax_amount && ex.tax > 0) patch.tax_amount = ex.tax;
    if (Object.keys(patch).length === 0) return;
    const r = await updateManufacturingReceiptHeader(receipt.id, patch);
    if (!r.ok) return;
    if (typeof patch.shipping_cost === "number") {
      setShippingDraft(String(r.data.receipt.shipping_cost));
    }
    if (typeof patch.tax_amount === "number") {
      setTaxDraft(String(r.data.receipt.tax_amount));
    }
    setAmountsSavedAt(new Date().toISOString());
    onReceiptMutated?.();
  }

  async function postRow(idx: number) {
    const draft = drafts[idx];
    if (!draft) return;
    setWorkingIdx(idx);
    const line = extracted?.line_items[idx];
    const suggestion = suggestions[idx] ?? null;
    const mat = materials.find((m) => m.id === draft.material_id);
    const quantity = convertOcrReceiptQtyToCanonical({
      enteredQty: Number(draft.quantity),
      material: mat,
      aliasUnitConversionFactor: draft.alias_unit_conversion_factor,
    });
    const r = await postMaterialCostEntry({
      material_id: draft.material_id,
      quantity,
      unit_cost: Number(draft.unit_cost),
      entry_type: "receive",
      notes: draft.notes || draft.description,
      receipt_id: receipt.id,
      // Slice 4g: silent learn-on-post. We send the hint whenever we
      // can identify the vendor + line description; the server then
      // decides whether to actually upsert based on whether the
      // operator's pick differs from the original suggestion.
      vendor_alias_hint:
        receipt.vendor_name && draft.description
          ? {
              vendor_name: receipt.vendor_name,
              vendor_sku: line?.vendor_sku ?? "",
              vendor_description: draft.description,
              was_suggested_material_id: suggestion?.material_id ?? null,
            }
          : null,
    });
    setWorkingIdx(null);
    if (!r.ok) {
      patchDraft(idx, { error: r.error });
      return;
    }
    patchDraft(idx, { posted_entry_id: r.data.entry_id, error: null });
    onReceiptMutated?.();
    void loadPosted();
  }

  async function toggleDismiss() {
    const nextDismissed = !receipt.unposted_dismissed_at;
    const r = await dismissReceiptUnposted(receipt.id, nextDismissed);
    if (!r.ok) {
      setError(`Could not ${nextDismissed ? "dismiss" : "re-surface"} receipt: ${r.error}`);
      return;
    }
    onReceiptMutated?.();
  }

  function patchDraft(idx: number, patch: Partial<ExtractedLineDraft>) {
    setDrafts((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  }

  const previewUrl = viewUrl;
  const extractedAtLabel = useMemo(() => {
    if (!receipt.extracted_at && !extracted) return "not yet extracted";
    if (!receipt.extracted_at) return "extraction pending";
    const d = new Date(receipt.extracted_at);
    return `extracted ${d.toLocaleString()} · ${receipt.extracted_model || "model"}`;
  }, [receipt.extracted_at, receipt.extracted_model, extracted]);

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center"
      onClick={onClose}
    >
      <div
        className={`max-w-3xl p-5 ${mfgModalShellClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-1.5">
            <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">
              Review receipt
            </h3>
            <ManufacturingReceiptHeaderEditors
              vendorDraft={vendorDraft}
              vendorSuggestions={vendorSuggestions}
              vendorSavedAt={vendorSavedAt}
              onVendorChange={setVendorDraft}
              onVendorCommit={(next) => void commitVendor(next)}
              shippingDraft={shippingDraft}
              onShippingChange={setShippingDraft}
              onShippingBlur={(next) => void commitAmount("shipping_cost", next)}
              taxDraft={taxDraft}
              onTaxChange={setTaxDraft}
              onTaxBlur={(next) => void commitAmount("tax_amount", next)}
              amountsSavedAt={amountsSavedAt}
              receiptDate={receipt.receipt_date}
              extractedAtLabel={extractedAtLabel}
            />
          </div>
          <div className="flex flex-col items-end gap-1">
            <button type="button" onClick={onClose} className={mfgXsSecondaryBtnClass}>
              Close
            </button>
            <div className="flex items-center gap-1">
              <InlineHelp
                label="When to dismiss vs. post"
                placement="left"
                dismissKey="mfg.receipts.dismissVsPost"
              >
                Use <strong>Dismiss</strong> when the rest of this receipt's lines
                aren't going into inventory (mixed personal + business purchase,
                a receipt where only one line is relevant, etc). It silences the
                Inventory landing banner and the per-row chip for this receipt
                without forcing phantom posts. <strong>Re-surface</strong> brings
                it back into the needs-attention counter.
              </InlineHelp>
              <button
                type="button"
                onClick={() => void toggleDismiss()}
                className={mfgXsSecondaryBtnClass}
              >
                {receipt.unposted_dismissed_at ? "Re-surface alert" : "Dismiss alert"}
              </button>
            </div>
          </div>
        </header>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-gray-600 dark:text-zinc-400">
          {previewUrl ? (
            <a
              href={previewUrl}
              target="_blank"
              rel="noreferrer"
              className="rounded-md border border-gray-200 bg-white px-2 py-0.5 font-medium text-indigo-700 hover:bg-indigo-50 dark:border-zinc-600 dark:bg-zinc-900 dark:text-indigo-300 dark:hover:bg-zinc-800"
            >
              Open file
            </a>
          ) : null}
          <button
            type="button"
            onClick={() => void runExtract({ force: true })}
            disabled={loading || reextracting}
            className={mfgXsSecondaryBtnClass}
          >
            {reextracting ? "Re-extracting…" : "Re-extract"}
          </button>
          <span className="ml-auto">
            {alreadyPosted.length} entr{alreadyPosted.length === 1 ? "y" : "ies"} already
            posted from this receipt
          </span>
        </div>

        {error ? (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="mt-4 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-6 text-center text-xs text-gray-600 dark:border-zinc-700 dark:bg-zinc-800/40 dark:text-zinc-400">
            Running OCR…
          </div>
        ) : extracted ? (
          <ManufacturingReceiptExtractionBody
            extracted={extracted}
            suggestions={suggestions}
            drafts={drafts}
            materials={localMaterials}
            workingIdx={workingIdx}
            onDraftChange={(idx, next) =>
              setDrafts((prev) => prev.map((d, i) => (i === idx ? next : d)))
            }
            onPost={postRow}
            onMaterialCreated={handleMaterialCreated}
            alreadyPosted={alreadyPosted}
          />
        ) : null}
      </div>
    </div>
  );
}

